import { useState, useEffect } from "react";
import { fetchData } from "../../api";
import { Link } from "react-router-dom";

export default function AllProjects({ limit }) {
    const [projects, setProjects] = useState([]);
    const [projectContents, setProjectContents] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    useEffect(() => {
        fetchData("our-project-contents", true).then(data => setProjectContents(data?.[0]));
        fetchData("our-projects-arrays", true).then(data => setProjects(data));
    }, []);

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isVideoModalOpen) {
                closeVideoModal();
            }
        };

        if (isVideoModalOpen) {
            document.addEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [isVideoModalOpen]);

    const openVideoModal = (video) => {
        setSelectedVideo(video);
        setIsVideoModalOpen(true);
    };

    const closeVideoModal = () => {
        setIsVideoModalOpen(false);
        setSelectedVideo(null);
    };

    const handleModalClick = (e) => {
        if (e.target === e.currentTarget) {
            closeVideoModal();
        }
    };

    if (!projectContents) return null;

    const visibleProjects = limit ? projects.slice(0, limit) : projects;

    return (
        <>
            <section className="relative py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.15)_1px,transparent_0)] bg-[length:20px_20px] opacity-30"></div>
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-[#286766]/10 to-transparent rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tl from-[#27352C]/10 to-transparent rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    {/* Header Section */}
                    <div className="text-center mb-20">
                        <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#286766] via-[#27352C] to-[#286766] bg-clip-text text-transparent mb-6">
                            {projectContents?.ProjectTitle}
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#286766] to-[#27352C] mx-auto rounded-full"></div>
                    </div>

                    {/* Projects Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {visibleProjects.map((project, index) => {
                            const imageUrl = project.Image?.[0]?.url || project.Image?.[0]?.data?.attributes?.url;
                            const videoUrl = project.Video?.url || project.Video?.data?.attributes?.url;
                            
                            return (
                                <div
                                    key={index}
                                    className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 overflow-hidden"
                                    style={{
                                        animationDelay: `${index * 0.1}s`,
                                        animation: 'fadeInUp 0.6s ease-out forwards'
                                    }}
                                >
                                    {/* Image Container */}
                                    <div className="relative overflow-hidden h-64">
                                        <img
                                            src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${imageUrl}`}
                                            alt={project.Title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />
                                        
                                        {/* Gradient Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                        
                                        {/* Play Button (only if video exists) */}
                                        {videoUrl && (
                                            <button
                                                onClick={() => openVideoModal(videoUrl)}
                                                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-75 group-hover:scale-100"
                                            >
                                                <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 border-2 border-white/30 hover:bg-white/30 transition-all duration-300">
                                                    <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M8 5v14l11-7z"/>
                                                    </svg>
                                                </div>
                                            </button>
                                        )}
                                        
                                        {/* Featured Badge */}
                                        <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-all duration-500 transform -translate-x-4 group-hover:translate-x-0">
                                            <span className="bg-gradient-to-r from-[#286766] to-[#27352C] text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                                                { project.BadgeText }
                                            </span>
                                        </div>
                                        
                                        {/* Decorative Corner */}
                                        <div className="absolute top-0 right-0 w-0 h-0 opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:w-8 group-hover:h-8 border-t-8 border-r-8 border-transparent border-t-[#286766] border-r-[#286766]"></div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#286766] transition-colors duration-300">
                                            {project.Title}
                                        </h3>
                                        <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                                            {project.Description}
                                        </p>
                                        
                                        {/* Action Buttons */}
                                        <div className="flex gap-3">
                                            <Link
                                                to={`/project/${index}`}
                                                className="flex-1 bg-gradient-to-r from-[#286766] to-[#27352C] text-white text-sm font-semibold py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                                            >
                                                {project.LinkText || "View Project"}
                                                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                </svg>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* View All Projects Button */}
                    {limit && projects.length > limit && (
                        <div className="text-center mt-12">
                            <Link
                                to="/projects"
                                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#286766] to-[#27352C] text-white font-semibold py-4 px-8 rounded-xl hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                            >
                            {projectContents?.ButtonText}
                                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {/* Video Modal */}
            {isVideoModalOpen && selectedVideo && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={handleModalClick}
                >
                    <div className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden shadow-2xl">
                        <button
                            onClick={closeVideoModal}
                            className="absolute top-4 right-4 z-10 bg-black/50 text-white rounded-full p-2 hover:bg-black/70 transition-colors duration-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <video
                            className="w-full h-auto"
                            controls
                            autoPlay
                            src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${selectedVideo}`}
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .line-clamp-3 {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </>
    );
}
